import React, { useState, useEffect } from 'react'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid2'
import IconButton from '@mui/material/IconButton'
import { FaMinimize, FaMaximize } from 'react-icons/fa6'
import defaultTexts from '../../../texts/texts'
import CustomSwitchField from '../../_common/_form_fields/CustomSwitchField'
import CustomCheckboxField from '../../_common/_form_fields/CustomCheckboxField'

const ConfigBillingPermissions = (props) => {
  const { billingPermissions, setBillingPermissions, getDefaultBillingPermissions } = props
  const theme = useTheme()

  const [expanded, setExpanded] = useState(true)
  const [allowManagement, setAllowManagement] = useState(false)
  const [configsRead, setConfigsRead] = useState(false)
  const [analyticsRead, setAnalyticsRead] = useState(false)
  const [depositsRead, setDepositsRead] = useState(false)
  const [depositsUpdate, setDepositsUpdate] = useState(false)
  const [servicesRead, setServicesRead] = useState(false)
  const [servicesCreate, setServicesCreate] = useState(false)
  const [servicesUpdate, setServicesUpdate] = useState(false)
  const [servicesDelete, setServicesDelete] = useState(false)
  const [pricesRead, setPricesRead] = useState(false)
  const [pricesUpdate, setPricesUpdate] = useState(false)
  const [pricesCreate, setPricesCreate] = useState(false)

  const wrapperStyle = {
    background: 'white',
    borderRadius: '50px 20px 20px 20px',
    boxShadow: '2px 2px 2px rgba(0, 0, 0, 0.25)',
    marginBottom: '40px',
  }

  const headStyle = {
    width: '100%',
    height: '60px',
  }

  const bodyStyle = {
    padding: '20px',
  }

  const labelStyle = {
    height: '60px',
    background: theme.palette.greenSheen,
    color: 'white',
    fontSize: '20px',
    fontWeight: '500',
    borderRadius: '50px 0px',
    padding: '0px 40px',    
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const fieldBoxStyle = {
    padding: '12px 0px'
  }

  const switchLabelStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '16px',
    fontWeight: '500',
  }

  const checkboxStyle = {
    color: theme.palette.violetsAreBlue,

    '&.Mui-checked': {
      color: theme.palette.violetsAreBlue,
    },

    '& .MuiSvgIcon-root': {
      fontSize: 24,
    },
  }

  const checkboxLabelStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '16px',
  }

  const updatePermission = (section, permission, value) => {
    let updated_custom_admin_ui = []

    billingPermissions.custom_admin_ui.forEach(s => {
      if (s.section === section) {
        let updated_permissions = []

        if (value) {
          updated_permissions = [...s.permissions, permission]
        } else {
          updated_permissions = s.permissions.filter(p => p !== permission)
        }

        updated_custom_admin_ui.push({ ...s, permissions: updated_permissions })
      } else {
        updated_custom_admin_ui.push(s)
      }
    })
    
    setBillingPermissions(prev => {
      return { ...prev, custom_admin_ui: updated_custom_admin_ui }
    })
  }

  const handleChangeAllowManagement = (event) => {
    const value = event.target.checked
    setAllowManagement(value)

    if (value) {
      setBillingPermissions(getDefaultBillingPermissions())
    } else {
      setBillingPermissions({
        visible_to_service: ['custom_client_ui'],
        custom_admin_ui: [],
      })
    }
  }

  const handleChangeConfigsRead = (value) => {
    setConfigsRead(value)
    updatePermission('billing_configs', 'read', value)
  }

  const handleChangeAnalyticsRead = (value) => {
    setAnalyticsRead(value)
    updatePermission('billing_analytics', 'read', value)
  }

  const handleChangeDepositsRead = (value) => {
    setDepositsRead(value)
    updatePermission('billing_deposits', 'read', value)
  }

  const handleChangeDepositsUpdate = (value) => {
    setDepositsUpdate(value)
    updatePermission('billing_deposits', 'update', value)
  }

  const handleChangeServicesRead = (value) => {
    setServicesRead(value)
    updatePermission('billing_services', 'read', value)
  }

  const handleChangeServicesCreate = (value) => {
    setServicesCreate(value)
    updatePermission('billing_services', 'create', value)
  }

  const handleChangeServicesUpdate = (value) => {
    setServicesUpdate(value)
    updatePermission('billing_services', 'update', value)
  }

  const handleChangeServicesDelete = (value) => {
    setServicesDelete(value)
    updatePermission('billing_services', 'delete', value)
  }

  const handleChangePricesRead = (value) => {
    setPricesRead(value)
    updatePermission('billing_prices', 'read', value)
  }

  const handleChangePricesUpdate = (value) => {
    setPricesUpdate(value)
    updatePermission('billing_prices', 'update', value)
  }

  const handleChangePricesCreate = (value) => {
    setPricesCreate(value)
    updatePermission('billing_prices', 'create', value)
  }

  useEffect(() => {
    const isPermission = (section, permission) => {
      let result = false

      if (billingPermissions && billingPermissions.custom_admin_ui && billingPermissions.custom_admin_ui.length > 0) {
        const section_object = billingPermissions.custom_admin_ui.find(item => item.section === section)
        if (section_object && section_object.permissions && section_object.permissions.length > 0 && section_object.permissions.includes(permission)) {
          result = true
        }
      }

      return result
    }

    if (billingPermissions && billingPermissions.visible_to_service && billingPermissions.visible_to_service.length > 0 && billingPermissions.visible_to_service.includes('custom_admin_ui')) {
      setAllowManagement(true)
    } else {
      setAllowManagement(false)
    }

    setConfigsRead(isPermission('billing_configs', 'read'))
    setAnalyticsRead(isPermission('billing_analytics', 'read'))
    setDepositsRead(isPermission('billing_deposits', 'read'))
    setDepositsUpdate(isPermission('billing_deposits', 'update'))
    setServicesRead(isPermission('billing_services', 'read'))
    setServicesCreate(isPermission('billing_services', 'create'))
    setServicesUpdate(isPermission('billing_services', 'update'))
    setServicesDelete(isPermission('billing_services', 'delete'))
    setPricesRead(isPermission('billing_prices', 'read'))
    setPricesUpdate(isPermission('billing_prices', 'update'))
    setPricesCreate(isPermission('billing_prices', 'create'))
  }, [billingPermissions])

  return (
    <Box sx={wrapperStyle}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={headStyle}
      >
        <Box sx={labelStyle}>
          <Box>{defaultTexts.permissions}</Box>
        </Box>
        <Box sx={{ paddingRight: '10px ' }}>
          {
            expanded ?
            <IconButton onClick={() => setExpanded(false)}>
              <FaMinimize color={theme.palette.greenSheen} />
            </IconButton> :
            <IconButton onClick={() => setExpanded(true)}>
              <FaMaximize color={theme.palette.greenSheen} />
            </IconButton>
          }
        </Box>
      </Stack>

      {
        expanded &&
        <Box sx={bodyStyle}>
          <Box sx={fieldBoxStyle}>
            <Stack
              direction='row'
              justifyContent='center'
            >
              <CustomSwitchField
                label={defaultTexts.allowBillingManagementText}
                checked={allowManagement}
                handleChange={handleChangeAllowManagement}
                sxLabel={switchLabelStyle}
              />
            </Stack>
          </Box>

          {
            allowManagement &&
            <Grid container>
              <Grid size={{ lg: 4 }}>

                <Box>
                  <CustomCheckboxField
                    label={defaultTexts.displayBillingServiceConfiguration}
                    checked={configsRead}
                    handleChange={handleChangeConfigsRead}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Box>

                <Box>
                  <CustomCheckboxField
                    label={defaultTexts.displayBillingServiceAnalytics}
                    checked={analyticsRead}
                    handleChange={handleChangeAnalyticsRead}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Box>

                <Box>
                  <CustomCheckboxField
                    label={defaultTexts.displayUserAccounts}
                    checked={depositsRead}
                    handleChange={handleChangeDepositsRead}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Box>

                <Box>
                  <CustomCheckboxField
                    label={defaultTexts.updateUserAccounts}
                    checked={depositsUpdate}
                    handleChange={handleChangeDepositsUpdate}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Box>

              </Grid>
              <Grid size={{ lg: 5 }}>

                <Box>
                  <CustomCheckboxField
                    label={defaultTexts.displayOpenStackServiceConfiguration}
                    checked={servicesRead}
                    handleChange={handleChangeServicesRead}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Box>

                <Box>
                  <CustomCheckboxField
                    label={defaultTexts.addNewOpenStackServiceToBilling}
                    checked={servicesCreate}
                    handleChange={handleChangeServicesCreate}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Box>

                <Box>
                  <CustomCheckboxField
                    label={defaultTexts.updateOpenStackServiceConfiguration}
                    checked={servicesUpdate}
                    handleChange={handleChangeServicesUpdate}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Box>

                <Box>
                  <CustomCheckboxField
                    label={defaultTexts.deleteOpenStackServiceFromBilling}
                    checked={servicesDelete}
                    handleChange={handleChangeServicesDelete}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Box>

              </Grid>
              <Grid size={{ lg: 3 }}>

                <Box>
                  <CustomCheckboxField
                    label={defaultTexts.displayPrices}
                    checked={pricesRead}
                    handleChange={handleChangePricesRead}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Box>

                <Box>
                  <CustomCheckboxField
                    label={defaultTexts.updatePrices}
                    checked={pricesUpdate}
                    handleChange={handleChangePricesUpdate}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Box>

                <Box>
                  <CustomCheckboxField
                    label={defaultTexts.addNewPrices}
                    checked={pricesCreate}
                    handleChange={handleChangePricesCreate}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Box>

              </Grid>
            </Grid>
          }
        </Box>
      }
    </Box>
  )
}

export default ConfigBillingPermissions